import '@/locales/i18n';
import './fonts.css';

import React, { type ReactNode } from 'react';

import { unstableSetRender } from 'antd-mobile';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import ReactDOM, { type Root } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';

import { QueryClientProvider } from '@tanstack/react-query';

import { ApplicationThemeWrapper } from './ApplicationThemeWrapper';
// import * as Sentry from '@sentry/react';
import Auth0Provider from './components/AuthWrapper/Auth0Provider';
import { DiagnosticModeProvider } from './contexts/DiagnosticModeContext';
import { GlobalProvider } from './contexts/GlobalContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { queryClient } from './queryClient';
import { router } from './router';
import { getEnvironment } from './utils/helpers';

type ContainerWithReactRoot = (Element | DocumentFragment) & { _reactRoot?: Root };

unstableSetRender((node: ReactNode, container: Element | DocumentFragment) => {
  const host = container as ContainerWithReactRoot;
  host._reactRoot ||= ReactDOM.createRoot(container);
  const root = host._reactRoot;
  root.render(node);
  return async () => {
    await new Promise((resolve) => setTimeout(resolve, 0));
    root.unmount();
  };
});

const idle =
  window.requestIdleCallback ||
  ((cb: () => void) => {
    setTimeout(cb, 0);
  });

if (import.meta.env.VITE_SENTRY_ENABLED !== 'false') {
  idle(async () => {
    const Sentry = await import('@sentry/react');

    Sentry.init({
      dsn: 'https://520371275e2c7bb21bf67b160a607bc5@o188333.ingest.us.sentry.io/4507929729302528',
      environment: getEnvironment(),
      integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
      // Tracing
      tracePropagationTargets: [],
      tracesSampleRate: 1.0, //  Capture 100% of the transactions
      //   // Set profilesSampleRate to 1.0 to profile every transaction.
      //   // Since profilesSampleRate is relative to tracesSampleRate,
      //   // the final profiling rate can be computed as tracesSampleRate * profilesSampleRate
      //   // For example, a tracesSampleRate of 0.5 and profilesSampleRate of 0.5 would
      //   // results in 25% of transactions being profiled (0.5*0.5=0.25)
      profilesSampleRate: 1.0,
      // Session Replay
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
    });
  });
}

let LDProvider: React.ComponentType<{ children: React.ReactNode }>;

const initializeLDProvider = async () => {
  LDProvider = await asyncWithLDProvider({
    clientSideID: import.meta.env.VITE_LAUNCH_DARKLY_CLIENT_ID ?? '65c2a16accc9860ed7be83cb', //draco
    timeout: 5, // 5 second timeout (default)
  });
};
initializeLDProvider().finally(() => {
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <LDProvider>
        <GlobalProvider>
          <ThemeProvider>
            <ApplicationThemeWrapper>
              <QueryClientProvider client={queryClient}>
                <Auth0Provider>
                  <DiagnosticModeProvider>
                    <RouterProvider router={router} />
                  </DiagnosticModeProvider>
                </Auth0Provider>
              </QueryClientProvider>
            </ApplicationThemeWrapper>
          </ThemeProvider>
        </GlobalProvider>
      </LDProvider>
    </React.StrictMode>,
  );
});
